// ─── coutAnalyses.js ─────────────────────────────────────────────────
// Ce que la cliente paie pour ses analyses.
//
// Anissa avance les frais au laboratoire puis refacture la cliente, et le
// crédit d'analyses inclus dans le pack se consomme là-dessus. Une erreur ici
// est une perte sèche pour elle, ou une facture injuste pour la cliente.
//
// Le calcul vit hors du composant d'écran pour rester vérifiable.

/**
 * Arrondi au franc supérieur, décidé le 22.08.2026.
 *
 * Les prix d'Ortho tombent sur des centimes (395.90, 300.90, 21.60) et une
 * facture à 417.50 se lit mal. On arrondit CE QUI EST FACTURÉ, jamais
 * `cost_anissa_chf` en base : ce champ reste la valeur exacte payée au labo,
 * donc la pièce justificative et la base pour vérifier une facture Ortho.
 *
 * Toujours vers le haut : l'écart est de quelques francs par prescription et
 * il va à Anissa. Un arrondi vers le bas lui ferait perdre de l'argent en
 * silence, et « arrondi au franc supérieur » s'assume à voix haute si une
 * cliente pose la question.
 */
export function auFrancSuperieur(n) {
  return Math.ceil(Number(n) || 0);
}

/**
 * Ce que la cliente paie, à partir des analyses retenues et des frais de bon.
 *
 * @param {Array<{code: string, cost_anissa_chf: number}>} tests
 * @param {number} totalTax   - frais de bons, déjà calculés par labFees
 * @param {number} creditChf  - crédit d'analyses inclus dans le pack
 * @returns {{
 *   analysesCost: number,
 *   totalCost: number,
 *   creditApplied: number,
 *   costForClient: number,
 *   coveredCodes: Set<string>,
 * }}
 */
export function calculerCoutFacture(tests = [], totalTax = 0, creditChf = 0) {
  const lignes = Array.isArray(tests) ? tests : [];
  const taxe = auFrancSuperieur(totalTax);
  const analysesCost = lignes.reduce((sum, t) => sum + auFrancSuperieur(t?.cost_anissa_chf), 0);
  const total = analysesCost + taxe;

  // Les frais de bons entament le crédit AVANT les analyses : ils sont dus
  // quoi qu'il arrive. Sans cette déduction, une analyse à 240 avec 22 de
  // frais et un crédit de 250 s'afficherait « incluse » alors qu'il reste
  // 12 à payer.
  const covered = new Set();
  if (creditChf > 0 && lignes.length > 0) {
    let remaining = Math.max(0, creditChf - taxe);
    for (const t of lignes) {
      const cost = auFrancSuperieur(t?.cost_anissa_chf);
      if (cost <= remaining) {
        covered.add(t?.code);
        remaining -= cost;
      }
    }
  }

  return {
    analysesCost,
    totalCost: total,
    creditApplied: Math.min(total, creditChf),
    costForClient: Math.max(0, total - creditChf),
    coveredCodes: covered,
  };
}
